import { useMemo } from 'react';
import { useLocation } from 'react-router';

import { useOpenClose } from '~/core/hooks/useOpenClose';
import { MIDDLE_OFFICE_ROUTES } from '~/core/middle-office/routing/routes';
import { useLogout } from '~/core/shared/api/authentication';
import { useFeatures } from '~/core/shared/contexts/features';
import { useUserData } from '~/core/shared/contexts/UserContext';
import { useCan } from '~/core/shared/hooks/useCan';
import { TenantNavigationItemType } from '../../../../../config';
import { useMiddleOfficeConfig } from '../../../../../contexts/middleOfficeConfig';
import { NavItem } from '../../types/NavItem';
import { configToNavItem } from './logic/configToNavItem';

export const useHeader = () => {
  const mobileNavCollapseApi = useOpenClose();
  const { getFeature } = useFeatures();
  const { user } = useUserData();
  const { meetsRequirement } = useCan();
  const logout = useLogout(MIDDLE_OFFICE_ROUTES.login.path);
  const middleOfficeConfig = useMiddleOfficeConfig();
  const { pathname } = useLocation();

  const navigationItems = useMemo(
    () =>
      middleOfficeConfig
        .getNavigationItems()
        .filter(({ feature, permission }) => {
          const appHasFeature = feature ? getFeature(feature)?.enabled : true;
          const userHasPermission = permission
            ? meetsRequirement(permission)
            : true;

          return appHasFeature && userHasPermission;
        }),
    [middleOfficeConfig.getNavigationItems, user],
  );

  const navItems: NavItem[] = useMemo(
    () =>
      navigationItems
        .filter((item) => item.type !== TenantNavigationItemType.Home)
        .map((item) => configToNavItem(item, pathname)),
    [navigationItems, pathname],
  );

  const homeNavItem = useMemo(() => {
    const homeNavigationItem = navigationItems.find(
      (item) => item.type === TenantNavigationItemType.Home,
    );

    if (homeNavigationItem) {
      return configToNavItem(homeNavigationItem);
    }

    return undefined;
  }, [navigationItems, configToNavItem]);

  return {
    mobileNavCollapseApi,
    middleOfficeConfig,
    homeNavItem,
    navItems,
    user,
    logout,
  };
};
